import React, { useState, useEffect, useRef } from "react";
import { ref, onValue, update } from 'firebase/database';
import { database, firestore } from '../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import { CLOUD_FUNCTION_URLS } from '../../utils/cloudFunctions';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

const InboxModeEmail = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply, 
  source, 
  onEmailResult,
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping
}) => {
  // Detailliertes Logging der eingehenden Daten
  console.log(`📧 [INBOX-MODE] Component loaded with data:`, {
    source,
    isReply,
    hasOriginalEmail: !!rowData.original_email,
    hasDraft: !!rowData.draft,
    hasDraftOriginalEmail: !!rowData.draft?.originalEmail,
    rowData: rowData
  });

  if (rowData.draft) {
    console.log(`📧 [INBOX-MODE] Draft data details:`, {
      draftId: rowData.draft.id,
      agentName: rowData.draft.agentName,
      hasOriginalEmail: !!rowData.draft.originalEmail,
      originalEmailSubject: rowData.draft.originalEmail?.subject,
      originalEmailFrom: rowData.draft.originalEmail?.from,
      originalEmailContent: rowData.draft.originalEmail?.content,
      originalEmailBodyText: rowData.draft.originalEmail?.bodyText
    });
  }

  if (rowData.original_email) {
    console.log(`📧 [INBOX-MODE] Original email data:`, {
      subject: rowData.original_email.subject,
      from: rowData.original_email.from,
      content: rowData.original_email.content,
      date: rowData.original_email.date
    });
  }

  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("Professional & Supportive");
  const [contextInfo, setContextInfo] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [showBlockingOverlay, setShowBlockingOverlay] = useState(false);
  
  // Ref für automatische Generierung
  const hasAutoGenerated = useRef(false);
  // Ref für das Betreff-Input-Element
  const subjectInputRef = useRef(null);

  // Firebase Functions
  const functions = getFunctions();
  const markEmailAsReplied = httpsCallable(functions, 'markEmailAsReplied');

  // Erweiterte Funktion zum Markieren der ursprünglichen E-Mail als beantwortet
  const markOriginalEmailAsReplied = async (emailId, isFromDraft = false) => {
    if (!emailId || !userId) {
      console.warn(`📧 [MARK-REPLIED] Missing emailId or userId`);
      return;
    }
    
    try {
      console.log(`📧 [MARK-REPLIED] Marking original email ${emailId} as replied`);
      
      // ✅ NEU: Rufe NUR die Cloud-Funktion auf (kein RTDB Update mehr)
      // Die Cloud-Funktion markiert die E-Mail in Firestore als beantwortet
      
      let accountId = rowData.accountId;
      let isSpam = false;
      
      // Für Drafts: Ermittle den korrekten Account
      if (isFromDraft && rowData.draft) {
        accountId = rowData.draft.originalEmail?.accountId || rowData.accountId;
        isSpam = rowData.draft.originalEmailPath === 'spam';
      }
      
      // Rufe die Cloud-Funktion auf, um den Status in Firestore und auf dem E-Mail-Server zu aktualisieren
      await markEmailAsReplied({ 
        emailId: emailId, 
        accountId: accountId,
        isSpam: isSpam
      });
      
      console.log(`📧 [MARK-REPLIED] Successfully marked email ${emailId} as replied`);
      
    } catch (error) {
      console.error(`📧 [MARK-REPLIED] Error marking email ${emailId} as replied:`, error);
      // Fehler nicht weiterwerfen, da die E-Mail trotzdem erfolgreich gesendet wurde
    }
  };

  // Setze die E-Mail-Adresse des Empfängers
  useEffect(() => {
    // Für Forward: Verwende die eingegebene E-Mail-Adresse
    if (rowData.lead_type === "email_forward" && rowData.lead_email) {
      setSelectedEmail(rowData.lead_email);
      console.log(`🔄 [FORWARD] Recipient email set for forward:`, {
        forwardEmail: rowData.lead_email,
        leadType: rowData.lead_type
      });
    }
    // Für normale Reply: Verwende die Original-E-Mail From-Adresse
    else if (rowData.original_email && rowData.original_email.from) {
      // Extrahiere die E-Mail-Adresse aus dem From-Feld
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
      const match = rowData.original_email.from.match(emailRegex);
      
      if (match && match[1]) {
        setSelectedEmail(match[1]);
      }
    }
    // Für Drafts: Setze die E-Mail-Adresse aus der Original-E-Mail
    else if (rowData.draft && rowData.draft.originalEmail && rowData.draft.originalEmail.from) {
      const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
      const match = rowData.draft.originalEmail.from.match(emailRegex);
      
      if (match && match[1]) {
        setSelectedEmail(match[1]);
        console.log(`📝 [DRAFT-EMAIL] Email address set for draft:`, {
          draftId: rowData.draft.id,
          recipientEmail: match[1],
          originalFrom: rowData.draft.originalEmail.from
        });
      }
    }
  }, [rowData]);

  // Setze die Absender-E-Mail basierend auf dem Account, mit dem die E-Mail empfangen wurde
  useEffect(() => {
    if (rowData.accountEmail && senderEmails.length > 0) {
      const matchingEmail = senderEmails.find(email => email.email === rowData.accountEmail);
      if (matchingEmail) {
        setSelectedSenderEmail(matchingEmail.email);
      } else if (senderEmails.length > 0) {
        setSelectedSenderEmail(senderEmails[0].email);
      }
    } 
    // Für Drafts: Versuche die Account-E-Mail aus der Original-E-Mail zu ermitteln
    else if (rowData.draft && rowData.draft.originalEmail && senderEmails.length > 0) {
      // Prüfe, ob die Original-E-Mail Account-Informationen hat
      const originalAccountEmail = rowData.draft.originalEmail.accountEmail;
      if (originalAccountEmail) {
        const matchingEmail = senderEmails.find(email => email.email === originalAccountEmail);
        if (matchingEmail) {
          setSelectedSenderEmail(matchingEmail.email);
          console.log(`📝 [DRAFT-SENDER] Sender email set for draft:`, {
            draftId: rowData.draft.id,
            senderEmail: matchingEmail.email,
            originalAccountEmail: originalAccountEmail
          });
        } else if (senderEmails.length > 0) {
          setSelectedSenderEmail(senderEmails[0].email);
        }
      } else if (senderEmails.length > 0) {
        setSelectedSenderEmail(senderEmails[0].email);
      }
    }
    else if (senderEmails.length > 0) {
      setSelectedSenderEmail(senderEmails[0].email);
    }
  }, [rowData.accountEmail, rowData.draft, senderEmails]);

  // Initialisiere Kontext basierend auf dem Modus
  useEffect(() => {
    if (isReply && rowData) {
      if (source === 'draft' && rowData.draft && rowData.draft.originalEmail) {
        // Für Drafts: Zeige die E-Mail-Historie wie im Bulk-Modus
        const originalEmail = rowData.draft.originalEmail;
        const emailHistory = `
Original E-Mail:
Von: ${originalEmail.from || 'Unbekannt'}
Datum: ${new Date(originalEmail.date || Date.now()).toLocaleString()}
Betreff: ${originalEmail.subject || ''}

${originalEmail.content || originalEmail.bodyText || originalEmail.body || ''}

Draft erstellt von Agent: ${rowData.draft.agentName} (Case ${rowData.draft.agentCaseNumber})
Draft-Aufgabe: ${rowData.draft.agentTask || 'Keine Aufgabe definiert'}
        `.trim();
        
        setContextInfo(emailHistory);
      } else if (rowData.original_email) {
        // Für normale E-Mail-Antworten
        const originalEmail = rowData.original_email;
        const emailHistory = `
Original E-Mail:
Von: ${originalEmail.from || 'Unbekannt'}
Datum: ${new Date(originalEmail.timestamp || originalEmail.date || Date.now()).toLocaleString()}
Betreff: ${originalEmail.subject || ''}

${originalEmail.content || originalEmail.bodyText || originalEmail.body || ''}
        `.trim();
        
        setContextInfo(emailHistory);
      } else {
        // Fallback für andere Fälle
        const contextText = `
Business Name: ${rowData.business_name || ''}
Description: ${rowData.lead_description || ''}
        `.trim();
        setContextInfo(contextText);
      }
    } else {
      // Für neue E-Mails (nicht Antworten)
      const contextText = `
Business Name: ${rowData.business_name || ''}
City: ${rowData.lead_city || ''}
Description: ${rowData.lead_description || ''}
Type: ${rowData.lead_type || ''}
Website: ${rowData.lead_website || ''}
      `.trim();
      setContextInfo(contextText);
    }
  }, [isReply, rowData, source]);

  // Setze den Betreff für Antworten
  useEffect(() => {
    if (isReply && rowData) {
      let originalSubject = '';
      
      if (source === 'draft' && rowData.draft && rowData.draft.originalEmail) {
        originalSubject = rowData.draft.originalEmail.subject || '';
      } else if (rowData.original_email) {
        originalSubject = rowData.original_email.subject || '';
      }
      
      if (originalSubject) {
        // 🛡️ [SAFEGUARD-RETRY] Überprüfe preventReplyPrefix Flag für SafeGuard Retry
        if (rowData.preventReplyPrefix === true || rowData.isSafeGuardRetry === true) {
          console.log('🛡️ [SUBJECT] Using original subject without "Re:" for SafeGuard retry');
          setSubjectLine(originalSubject);
        } else {
          // Normale Reply-Logik mit "Re:" 
          const replySubject = originalSubject.startsWith('Re:') 
            ? originalSubject 
            : `Re: ${originalSubject}`;
          setSubjectLine(replySubject);
        }
      }
    }
  }, [isReply, rowData, source]);

  // Prüfe, ob es sich um einen Draft handelt und fülle den Inhalt vor
  useEffect(() => {
    if (rowData.draft && rowData.draft.content) {
      console.log(`📝 [DRAFT-MODE] Loading draft content:`, {
        draftId: rowData.draft.id,
        agentName: rowData.draft.agentName,
        contentLength: rowData.draft.content.length
      });
      
      // Setze den Draft-Inhalt direkt
      setBodyContent(rowData.draft.content);
      
      // Verhindere automatische Generierung für Drafts
      hasAutoGenerated.current = true;
    }
    // Für Forward: Verwende den vorgefüllten Content
    else if (rowData.prefilledBodyContent && rowData.skipGeneration) {
      console.log(`🔄 [FORWARD-MODE] Loading prefilled forward content:`, {
        contentLength: rowData.prefilledBodyContent.length,
        leadType: rowData.lead_type
      });
      
      // Setze den Forward-Inhalt direkt
      setBodyContent(rowData.prefilledBodyContent);
      
      // Verhindere automatische Generierung für Forward
      hasAutoGenerated.current = true;
    }
  }, [rowData.draft, rowData.prefilledBodyContent, rowData.skipGeneration]);

  // Laden der benutzerdefinierten Antwort-Topics
  useEffect(() => {
    if (!isTopicsLoaded) return;
    
    // Suche nach benutzerdefinierten Antwort-Topics
    const customReplyTopics = Object.entries(userTopics)
      .filter(([_, data]) => data.type === 'custom_reply')
      .map(([id, data]) => ({
        id,
        name: data.topic,
        type: data.type
      }));
    
    if (customReplyTopics.length > 0) {
      setSelectedTopic(customReplyTopics[0].name);
    } else {
      // Fallback auf Standard-Topics
      setSelectedTopic("Reply To Mail");
    }
  }, [isTopicsLoaded, userTopics]);

  // Setze das vorausgewählte Topic, wenn vorhanden
  useEffect(() => {
    if (rowData.selectedTopic) {
      setSelectedTopic(rowData.selectedTopic);
    }
  }, [rowData.selectedTopic]);

  // Automatische Generierung beim ersten Laden (nur wenn es kein Draft ist)
  useEffect(() => {
    if (
      !hasAutoGenerated.current && 
      isTopicsLoaded && 
      selectedTopic && 
      selectedStyle && 
      !isGenerating &&
      contextInfo && // Nur wenn der Kontext (Original-E-Mail) geladen ist
      !rowData.draft // Keine automatische Generierung für Drafts
    ) {
      hasAutoGenerated.current = true;
      setTimeout(() => {
        handleGenerate();
      }, 500);
    }
  }, [isTopicsLoaded, selectedTopic, selectedStyle, isGenerating, contextInfo, rowData.draft]);

  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers. Gehe auf die konkreten Punkte ein.",
      'inbox_reply': "Verfasse eine höfliche und direkte Antwort auf die E-Mail. Gehe auf die wichtigsten Punkte ein und biete eine klare Lösung an.",
      'inbox_details': "Erstelle eine detaillierte E-Mail mit allen relevanten Informationen über unser Angebot. Erkläre die Vorteile und Preise.",
      'inbox_zoom': "Schlage ein Zoom-Meeting vor und biete mehrere mögliche Termine an. Erkläre kurz, was besprochen werden soll.",
      'inbox_meetup': "Schlage ein persönliches Treffen vor. Frage nach bevorzugten Orten und Zeiten.",
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle Antwort auf die E-Mail.";
  };

  // Spezifische Anweisungen für den Antworttyp holen
  const getToneInstructionsForReplyType = (replyType) => {
    // Prüfe, ob es ein benutzerdefiniertes Topic mit diesem Typ gibt
    const customTopic = Object.entries(userTopics).find(([_, data]) => data.type === replyType);
    if (customTopic && customTopic[1].topicSpecificNote) {
      return customTopic[1].topicSpecificNote;
    }
    
    // Fallback zu Standard-Anweisungen
    return getDefaultTopicNote(replyType);
  };

  // Topic-Typ basierend auf dem Namen ermitteln
  const getTopicType = (selectedTopicName) => {
    // Prüfe, ob es ein benutzerdefiniertes Topic ist
    const customTopic = Object.entries(userTopics).find(([_, data]) => data.topic === selectedTopicName);
    if (customTopic) {
      return customTopic[1].type;
    }
    
    // Mapping für Standard-Inbox-Topics
    const inboxTopicMapping = {
      "Reply To Mail": "inbox_reply",
      "Send Details": "inbox_details",
      "Schedule Zoom Call": "inbox_zoom",
      "Ask for Meetup": "inbox_meetup"
    };
    
    return inboxTopicMapping[selectedTopicName] || "custom_reply";
  };

  // E-Mail generieren
  const handleGenerate = async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    setBodyContent("");
    
    try {
      // Für Antworten: Hole die spezifische Notiz für den ausgewählten Topic-Typ
      const topicType = getTopicType(selectedTopic);
      let topicSpecificNote = "";
      
      const customTopic = Object.entries(userTopics).find(([_, data]) => data.topic === selectedTopic);
      topicSpecificNote = customTopic ? customTopic[1].topicSpecificNote || "" : "";
      
      // Wenn keine benutzerdefinierte Notiz gefunden wurde, verwende die Standard-Anweisungen
      if (!topicSpecificNote) {
        topicSpecificNote = getToneInstructionsForReplyType(topicType);
      }
      
      // KI-Modellparameter
      const model = aiSettings.model || 'gpt-4o-mini';
      const temperature = parseFloat(aiSettings.temperature) || 0.9;
      const maxTokens = parseInt(aiSettings.max_tokens) || 250;
      const language = aiSettings.language || 'German';
      const promptTemplate = aiSettings.promptTemplate || null;
      const strictInstructions = aiSettings.strictInstructions || null;

      const requestData = {
        topic: selectedTopic,
        style: selectedStyle,
        context: contextInfo,
        businessName: rowData.business_name || '',
        website: rowData.lead_website || '',
        senderName: userSettings.user_name || '',
        senderEmail: userSettings.user_email || '',
        firstName: rowData.lead_firstname || '',
        userId: userId,
        topicSpecificNote: topicSpecificNote,
        model: model,
        temperature: temperature,
        maxTokens: maxTokens,
        language: language,
        includeAllDetails: true,
        isReply: true, // Immer true für Inbox-Modus
        subjectLine: subjectLine,
        promptTemplate: promptTemplate,
        strictInstructions: strictInstructions,
        separateSubjectAndBody: true,
        excludeSubjectFromBody: true,
        bodyOnlyGeneration: true,
        preventSubjectInContent: true
      };

      const response = await fetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`E-Mail-Generierung fehlgeschlagen: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setBodyContent(data.emailContent);
      
      // Betreffzeile nur setzen, wenn sie leer ist
      if ((!subjectLine || subjectLine.trim() === '') && data.subjectLine) {
        setSubjectLine(data.subjectLine);
      }
    } catch (error) {
      console.error('Fehler bei der E-Mail-Generierung:', error);
      alert('Die E-Mail konnte nicht generiert werden. Bitte versuche es erneut.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Letzten Absender in der Datenbank speichern
  const saveLastSenderToDatabase = async (senderEmail) => {
    try {
      if (!userId || !senderEmail) return false;
      
      const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
      await update(lastSenderRef, {
        email: senderEmail,
        timestamp: Date.now(),
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Fehler beim Speichern des letzten Absenders:', error);
      return false;
    }
  };

  // E-Mail senden
  const handleSend = async () => {
    if (!selectedEmail) {
      onEmailResult('error', 'No recipient email address provided');
      return;
    }

    const emailId = rowData.id;
    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentSenderEmail = selectedSenderEmail;
    
    // 🔄 [FORWARD-FIX] Prüfe, ob es sich um eine Forward-E-Mail handelt
    const isForwardEmail = rowData.lead_type === "email_forward" || 
      rowData.isForwardOperation === true || 
      (emailId && (emailId.toString().startsWith('forward_') || emailId.toString().startsWith('temp_forward_')));
    
    console.log(`📧 [FORWARD-DEBUG] Email type check:`, {
      isForwardEmail,
      leadType: rowData.lead_type,
      isForwardOperation: rowData.isForwardOperation,
      emailId: emailId,
      startsWithForward: emailId && emailId.toString().startsWith('forward_'),
      startsWithTempForward: emailId && emailId.toString().startsWith('temp_forward_')
    });
    
    setIsSending(true);
    
    // Callback auslösen, bevor die E-Mail gesendet wird
    if (onEmailResult) {
      onEmailResult('success', 'Sending email...', rowData);
    }
    
    // E-Mail im Hintergrund senden
    (async () => {
      try {
        // Absender-E-Mail finden
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        
        if (!senderEmailData) {
          throw new Error("The selected sender email was not found.");
        }
        
        // E-Mail formatieren
        const formattedContent = convertToHtml(currentContent, userSettings);
        
        // Detailliertes Logging der userSettings vor dem Footer-Hinzufügen
        console.log(`📧 [FOOTER-DEBUG] userSettings before addFooterToEmail:`, {
          hasUserSettings: !!userSettings,
          footerText: userSettings?.footer_text,
          footerImageUrl: userSettings?.footer_image_url,
          footerFont: userSettings?.footer_font,
          footerImagePosition: userSettings?.footer_image_position,
          footerImageSize: userSettings?.footer_image_size,
          allUserSettings: userSettings
        });
        
        let contentWithFooter = addFooterToEmail(formattedContent, userSettings);
        
        console.log(`📧 [FOOTER-DEBUG] Content before footer:`, formattedContent?.substring(0, 200));
        console.log(`📧 [FOOTER-DEBUG] Content after footer:`, contentWithFooter?.substring(0, 500));
        
        // Füge die Konversationshistorie hinzu
        if (rowData.original_email) {
          const originalEmail = rowData.original_email;
          
          // Erstelle die Konversationshistorie
          const emailHistory = `
<div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #666;">
  <p style="margin-bottom: 10px; color: #888; font-style: italic;">Am ${new Date(originalEmail.date || originalEmail.timestamp || Date.now()).toLocaleString()} schrieb ${originalEmail.from || 'Unbekannt'}:</p>
  <blockquote style="margin: 0; padding-left: 10px; border-left: 2px solid #ddd; color: #666;">
    ${originalEmail.content || originalEmail.body || ''}
  </blockquote>
</div>
          `;
          
          // Füge die Konversationshistorie nach dem Footer hinzu
          contentWithFooter = `${contentWithFooter}\n\n${emailHistory}`;
        }
        
        // Request-Daten erstellen
        const requestData = {
          recipients: [currentSelectedEmail],
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: isForwardEmail ? (senderEmailData.sender_name || userSettings.user_name || 'Forward') : "E-Mail-Antwort",
          // 🔄 [FORWARD-FIX] Für Forward-E-Mails verwende keine leadId oder eine spezielle leadId
          leadId: isForwardEmail ? null : emailId,
          emailType: isForwardEmail ? "forward" : getTopicType(selectedTopic),
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id,
          forceImmediate: true, // Sofortige Sendung ohne Verzögerung erzwingen (nur für InboxMode)
          // 🔄 [FORWARD-FIX] Markiere Forward-E-Mails speziell
          isForward: isForwardEmail
        };
        
        console.log(`🔄 [FORWARD-DEBUG] Request data:`, {
          isForward: requestData.isForward,
          leadId: requestData.leadId,
          emailType: requestData.emailType,
          businessName: requestData.businessName
        });
        
        // E-Mail senden
        const response = await fetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Fehler beim Parsen der JSON-Antwort:', e);
          if (onEmailResult) {
            onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`, rowData);
          }
          return;
        }
        
        if (data.success) {
          // 🛡️ [SAFEGUARD] Prüfe, ob E-Mail von SafeGuard abgelehnt und als Draft gespeichert wurde
          if (data.drafted && data.safeGuardRating !== undefined) {
            // SafeGuard hat die E-Mail abgelehnt und als Draft gespeichert
            if (onEmailResult) {
              onEmailResult(
                'safeguard_rejected', 
                `Email quality rating: ${data.safeGuardRating}/${data.minimumRating || 10}. Saved as draft for manual review.`, 
                rowData, 
                null, 
                currentSelectedEmail
              );
            }
            return; // Stoppe hier, da die E-Mail nicht gesendet wurde
          }
          
          // Erfolg - normale Sendung
          if (onEmailResult) {
            // Prüfe, ob die E-Mail verzögert wurde und eine geplante Zeit hat
            if (data.delayed && data.scheduledTime) {
              // scheduledTime in einen gültigen Timestamp konvertieren
              let scheduledTimeStamp = null;
              try {
                scheduledTimeStamp = new Date(data.scheduledTime).getTime();
                console.log(`Geplante Zeit konvertiert: ${data.scheduledTime} -> ${scheduledTimeStamp}`);
              } catch (e) {
                console.error("Fehler bei der Konvertierung der geplanten Zeit:", e);
              }
              
              onEmailResult('success', isForwardEmail ? `Forward scheduled for later` : `Reply scheduled for later`, rowData, scheduledTimeStamp, currentSelectedEmail);
              console.log(`Email scheduled to be sent at: ${data.scheduledTime} to: ${currentSelectedEmail}`);
            } else {
              onEmailResult('success', isForwardEmail ? `Email successfully forwarded` : `Reply successfully sent`, rowData, null, currentSelectedEmail);
            }
          }
          
          // Letzten Absender speichern
          await saveLastSenderToDatabase(currentSenderEmail);
          
          // 🔥 NEU: Prüfe und setze AI Tag basierend auf dem verwendeten Topic - nur für normale Antworten, nicht für Forward
          if (rowData.id && !isForwardEmail) {
            await setAiTagAfterEmailSend(selectedTopic, rowData.id)
              .catch(error => console.error("Error setting AI tag after reply send:", error));
          }
          
          // Markiere die ursprüngliche E-Mail als beantwortet (sowohl lokal als auch auf dem Server) - nur für normale Antworten
          if (!isForwardEmail) {
            if (rowData.draft && rowData.draft.originalEmailId) {
              // Für Drafts: Markiere die ursprüngliche E-Mail als beantwortet
              await markOriginalEmailAsReplied(rowData.draft.originalEmailId, true);
            } else if (rowData.id) {
              // Für normale E-Mail-Antworten: Markiere die E-Mail als beantwortet
              await markOriginalEmailAsReplied(rowData.id, false);
            }
          } else {
            console.log(`🔄 [FORWARD-INFO] Skipping reply marking for forward email`);
          }
          
          // Wenn es sich um einen Draft handelt, aktualisiere den Draft-Status (nicht für Forward)
          if (rowData.draft && rowData.draft.id && !isForwardEmail) {
            try {
              console.log(`📝 [DRAFT-SENT] Updating draft status to 'sent':`, {
                draftId: rowData.draft.id,
                agentName: rowData.draft.agentName
              });
              
              const draftRef = ref(database, `/World/${userId}/emailDrafts/${rowData.draft.id}`);
              await update(draftRef, {
                status: 'sent',
                sentAt: new Date().toISOString(),
                sentTimestamp: Date.now(),
                sentTo: currentSelectedEmail,
                sentSubject: currentSubject
              });
              
              console.log(`📝 [DRAFT-SENT] Draft ${rowData.draft.id} successfully marked as sent`);
            } catch (draftError) {
              console.error(`📝 [DRAFT-ERROR] Failed to update draft status:`, draftError);
              // Nicht kritisch - E-Mail wurde trotzdem gesendet
            }
          }
          
          setSendSuccess(true);
          
          // 🔄 [AUTO-CLOSE] Automatisches Schließen bei erfolgreich gesendeten E-Mails
          console.log(`✅ [EMAIL-SUCCESS] Email sent successfully, auto-closing popup in 2 seconds`);
          
          // Kurze Verzögerung für User-Feedback, dann automatisch schließen
          setTimeout(() => {
            if (onClose) {
              onClose(true); // true = E-Mail wurde erfolgreich gesendet
            }
          }, 2000);
          
        } else {
          // Fehler
          let errorMessage = data.error || 'Unknown error sending email';
          
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `The email address "${senderEmailData.email}" was rejected by the server.`;
          }
          
          if (onEmailResult) {
            onEmailResult('error', errorMessage, rowData);
          }
        }
      } catch (error) {
        console.error("Error sending email:", error);
        
        if (onEmailResult) {
          onEmailResult('error', error.message || 'Unknown error sending email', rowData);
        }
      } finally {
        setIsSending(false);
      }
    })();
  };

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // 🔥 NEUE FUNKTION: AI Tag basierend auf Topic nach E-Mail-Versand setzen
  const setAiTagAfterEmailSend = async (topic, contactId) => {
    try {
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] Checking if topic has associated AI Tag:', topic);
      
      if (!userId || !contactId || !topic) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Missing required parameters');
        return false;
      }
      
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] No matching topic found in userTopics for:', topic);
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Available topics:', {
          allTopics: Object.values(userTopics).map(t => ({ topic: t.topic, type: t.type, hasAiTagId: !!t.aiTagId })),
          selectedTopics: userTopics.selectedTopics ? Object.keys(userTopics.selectedTopics) : 'none',
          customTopics: userTopics.customTopics ? Object.keys(userTopics.customTopics) : 'none'
        });
        return false;
      }
      
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] Found matching topic:', matchingTopic);
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Topic has no associated AI tag - this is normal');
        return false;
      }
      
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] ✅ Topic has associated AI tag ID:', matchingTopic.aiTagId);
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] AI tag not found for ID:', matchingTopic.aiTagId);
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] AI tag data loaded:', aiTagData);
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'post_email_send_reply',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);

      if (aiTagData.overwriteOtherTags === true) {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Overwrite tag detected, removing all other tags');
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        console.log('🏷️ [INBOX-POST-EMAIL-TAG] Adding tag alongside existing tags');
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      console.log('🏷️ [INBOX-POST-EMAIL-TAG] ✅ AI tag successfully applied after reply send');
      return true;
      
    } catch (error) {
      console.error('🏷️ [INBOX-POST-EMAIL-TAG] Error setting AI tag after reply send:', error);
      return false;
    }
  };

  return (
    <div className="emailAiPopupContainer reply-mode">
      <div className="emailAiPopupHeader">
        <h2>
          {rowData.draft ? (
            <>
              📝 Draft bearbeiten
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 'normal', 
                color: '#666', 
                marginLeft: '10px' 
              }}>
                von {rowData.draft.agentName}
              </span>
            </>
          ) : (
            'E-Mail-Antwort'
          )}
        </h2>
        <button className="emailAiPopupCloseButton" onClick={onClose}>×</button>
      </div>
      
      <div className="emailAiPopupContent">
        {/* LINKE SPALTE: Topics, Style, Context */}
        <div className="emailAiPopupLeftColumn">
          {/* Draft Info Section - nur anzeigen wenn es ein Draft ist */}
          {source === 'draft' && rowData.draft && (
            <div className="emailAiPopupTopicSection">
              <h3 className="emailAiPopupSectionTitle">Draft Info</h3>
              <div className="draft-info-container" style={{marginBottom: '15px'}}>
                <div className="draft-info-item">
                  <strong>Agent:</strong> {rowData.draft.agentName} (Case {rowData.draft.agentCaseNumber})
                </div>
                <div className="draft-info-item">
                  <strong>Created:</strong> {new Date(rowData.draft.createdAt || Date.now()).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {/* Topics nur anzeigen wenn es kein Draft ist */}
          {source !== 'draft' && (
            <div className="emailAiPopupTopicSection">
              <h3 className="emailAiPopupSectionTitle">Topic</h3>
              <div className="emailAiPopupTopicButtons">
                {/* Zeige benutzerdefinierte Antwort-Topics oder Standards */}
                {(() => {
                  const customReplyTopics = Object.entries(userTopics)
                    .filter(([_, data]) => data.type === 'custom_reply')
                    .map(([id, data]) => ({
                      id,
                      name: data.topic
                    }));
                  
                  const topicsToShow = customReplyTopics.length > 0 
                    ? customReplyTopics 
                    : [
                        { id: 'reply', name: 'Reply To Mail' },
                        { id: 'details', name: 'Send Details' },
                        { id: 'zoom', name: 'Schedule Zoom Call' },
                        { id: 'meetup', name: 'Ask for Meetup' }
                      ];
                  
                  return topicsToShow.map(topic => (
                    <button
                      key={topic.id}
                      className={`emailAiPopupTopicButton ${selectedTopic === topic.name ? "active" : ""}`}
                      onClick={() => setSelectedTopic(topic.name)}
                    >
                      {topic.name}
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}

          <div className="emailAiPopupStyleSection">
            <h3 className="emailAiPopupSectionTitle">Writing style</h3>
            <div className="emailAiPopupStyleButtons">
              <button
                onClick={() => setSelectedStyle("Professional & Supportive")}
                className={selectedStyle === "Professional & Supportive" ? "activeStyle" : ""}
              >
                <strong>Professional</strong>
                <span className="styleDescription">Formal, präzise, geschäftlich</span>
              </button>
              <button
                onClick={() => setSelectedStyle("Friendly & Authentic")}
                className={selectedStyle === "Friendly & Authentic" ? "activeStyle" : ""}
              >
                <strong>Casual</strong>
                <span className="styleDescription">Locker, warm, persönlich</span>
              </button>
            </div>
          </div>

          <div className="emailAiPopupContextSection">
            <h3 className="emailAiPopupSectionTitle">Email history and info</h3>
            <div className="emailAiPopupContextWrapper">
              <textarea
                className="emailAiPopupContextInput"
                value={contextInfo}
                onChange={(e) => setContextInfo(e.target.value)}
                readOnly
              ></textarea>
            </div>
          </div>
        </div>

        {/* RECHTE SPALTE: Subject, Body, Extra Buttons, Generate/Send */}
        <div className="emailAiPopupRight">
          {/* Absender-E-Mail-Auswahl */}
          <div className="emailAiPopupField">
            <label>From:</label>
            <div className="emailAiPopupSenderEmailsContainer">
              {senderEmails.length > 0 ? (
                <div className="emailAiPopupSenderEmailsScroll">
                  {/* Bei Inbox-E-Mails nur die aktuelle Account-E-Mail anzeigen */}
                  <button 
                    key={selectedSenderEmail}
                    className="emailAiPopupSenderEmailButton active"
                  >
                    {(() => {
                      const account = senderEmails.find(item => item.email === selectedSenderEmail);
                      return account && account.sender_name 
                        ? `${account.sender_name} <${account.email}>` 
                        : (account ? account.email : selectedSenderEmail);
                    })()}
                  </button>
                </div>
              ) : (
                <p className="emailAiPopupNoEmails">No sender emails configured.</p>
              )}
            </div>
          </div>

          {/* Empfänger-E-Mail-Anzeige */}
          <div className="emailAiPopupRecipientSection">
            <h3 className="emailAiPopupSectionTitle">To:</h3>
            <div className="emailAiPopupTopButtons">
              <div className="emailAiPopupButtonWrapper">
                <button
                  className="emailAiPopupButton active"
                >
                  {selectedEmail}
                </button>
              </div>
            </div>
          </div>

          {/* Betreff und Inhalt */}
          <input
            className="emailAiPopupSubjectInput"
            placeholder="Subject line"
            value={subjectLine}
            onChange={(e) => setSubjectLine(e.target.value)}
            ref={subjectInputRef}
          />

          <div className={`emailAiPopupTexfieldwrapper ${isGenerating ? 'loading' : ''}`} style={{position: 'relative'}}>
            <div className="salesgod-loading-container">
              <img src="/img/salesgod_dark.webp" alt="SalesGod" className="salesgod-loading-logo" />
              <span>SalesGod Generating</span>
            </div>
            <div className="emailAiPopupFormatButtons">
              <button 
                type="button" 
                onClick={() => {
                  const textarea = document.getElementById('emailContent');
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const selectedText = bodyContent.substring(start, end);
                  const newText = bodyContent.substring(0, start) + 
                                 `<b>${selectedText}</b>` + 
                                 bodyContent.substring(end);
                  setBodyContent(newText);
                }}
                title="Fett"
              >
                B
              </button>
              <button 
                type="button" 
                onClick={() => {
                  const textarea = document.getElementById('emailContent');
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const selectedText = bodyContent.substring(start, end);
                  const newText = bodyContent.substring(0, start) + 
                                 `<i>${selectedText}</i>` + 
                                 bodyContent.substring(end);
                  setBodyContent(newText);
                }}
                title="Kursiv"
              >
                I
              </button>
            </div>
            <textarea
              id="emailContent"
              className="emailAiPopupTextArea"
              value={bodyContent}
              onChange={(e) => setBodyContent(e.target.value)}
              placeholder="Schreibe deine Antwort hier..."
            ></textarea>
          </div>
          <div className="emailAiPopupBottomButtons">
            <button 
              className={`emailAiPopupGenerateButton ${isGenerating ? 'loading' : ''}`} 
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <span className="emailAiPopupSpinner"></span>
              ) : (
                <img src="/img/star.svg" alt="AI" className="emailAiPopupGenerateIcon" />
              )}
            </button>
            <button
              className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() ? 'disabled' : ''}`}
              onClick={handleSend}
              disabled={isGenerating || isSending || !isEmailValid()}
            >
              {isSending ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Sending...</span>
                </>
              ) : sendSuccess ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>Send Reply</span>
                  <img src="/img/sent.svg" alt="Send" className="emailAiPopupSendIcon" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboxModeEmail; 